import { injectGlobal, CSSObject } from '@emotion/css';
import { mqBreakpoints } from '../constants/media-query-breakpoints';
import { pxToRem } from '../utils';

const headingBase: CSSObject = {
  fontFamily: 'var(--font-heading)',
  fontWeight: 'var(--font-weight-medium)',
  margin: 0,
  maxWidth: 'var(--max-content-width)',
  letterSpacing: 0,
  paddingBottom: pxToRem(32),
};

const headingLightBase: CSSObject = {
  ...headingBase,
  fontFamily: 'var(--font-heading-light)',
  fontWeight: 'var(--font-weight-thin)',
  maxWidth: '100%',
};

const headingLoopBase: CSSObject = {
  ...headingBase,
  fontFamily: 'var(--font-loop)',
  fontWeight: 'var(--font-weight-regular)',
  maxWidth: '100%',
};

const headingStraplineBase: CSSObject = {
  ...headingBase,
  fontWeight: 'var(--font-weight-regular)',
};

const headingDisplayBase: CSSObject = {
  ...headingLoopBase,
  fontWeight: 'var(--font-weight-regular)',
  paddingBottom: pxToRem(64),
  maxWidth: '100%',
};

const headingDisplayLightBase: CSSObject = {
  ...headingLightBase,
  // fontWeight: 'var(--font-weight-lightest)',
  paddingBottom: pxToRem(64),
  maxWidth: '100%',
};

// Headings

injectGlobal({
  h1: {
    ...headingLoopBase,
    fontFamily: 'var(--font-loop)',
    fontSize: pxToRem(48),
    lineHeight: pxToRem(56),
    letterSpacing: '-0.65px',
    paddingBottom: pxToRem(48),
  },
  h2: {
    ...headingLoopBase,
    fontFamily: 'var(--font-loop)',
    fontSize: pxToRem(36),
    lineHeight: pxToRem(44),
    letterSpacing: '-0.37px',
    paddingBottom: pxToRem(40),
  },
  h3: {
    ...headingBase,
    fontSize: pxToRem(28),
    lineHeight: pxToRem(36),
    letterSpacing: '-0.25px',
    paddingBottom: pxToRem(40),
  },
  h4: {
    ...headingBase,
    fontSize: pxToRem(24),
    lineHeight: pxToRem(32),
    letterSpacing: '-0.25px',
  },
  h5: {
    ...headingBase,
    fontSize: pxToRem(20),
    lineHeight: pxToRem(28),
  },
  h6: {
    ...headingBase,
    fontSize: pxToRem(16),
    lineHeight: pxToRem(24),
  },

  [mqBreakpoints.tablet]: {
    h1: {
      fontSize: pxToRem(64),
      lineHeight: pxToRem(72),
      letterSpacing: '-1px',
      paddingBottom: pxToRem(64),
    },
    h2: {
      fontSize: pxToRem(48),
      lineHeight: pxToRem(56),
      letterSpacing: '-0.75px',
      paddingBottom: pxToRem(48),
    },
    h3: {
      fontSize: pxToRem(32),
      lineHeight: pxToRem(40),
      letterSpacing: '-0.5px',
      paddingBottom: pxToRem(40),
    },
    h5: {
      letterSpacing: '-0.25px',
    },
    h6: {
      fontSize: pxToRem(18),
      paddingBottom: pxToRem(24),
    },
  },
});

// Display Headings - Desktop only

injectGlobal({
  [mqBreakpoints.desktop]: {
    '.display-1': {
      ...headingDisplayBase,
      fontSize: pxToRem(96),
      lineHeight: pxToRem(112),
      letterSpacing: '-2px',
    },
    '.display-2': {
      ...headingDisplayBase,
      fontSize: pxToRem(80),
      lineHeight: pxToRem(88),
      letterSpacing: '-1.5px',
    },
    '.display-3': {
      ...headingDisplayBase,
      fontSize: pxToRem(72),
      lineHeight: pxToRem(80),
      letterSpacing: '-1.5px',
    },
    '.display-4': {
      ...headingDisplayBase,
      fontSize: pxToRem(64),
      lineHeight: pxToRem(72),
      letterSpacing: '-1px',
    },
    '.display-4-light': {
      ...headingDisplayLightBase,
      fontSize: pxToRem(64),
      lineHeight: pxToRem(72),
      letterSpacing: '-1px',
    },
  },
});

// Allow for smaller font to be displayed on tablet and mobile, not only on desktop
injectGlobal({
  '.display-4-light-all': {
    ...headingDisplayLightBase,
  },
});

// Straplines

const displayStrapline: CSSObject = {
  ...headingStraplineBase,
  fontSize: pxToRem(28),
  lineHeight: pxToRem(36),
  letterSpacing: '-0.25px',
  marginTop: pxToRem(-32),
  paddingBottom: pxToRem(64),
};

injectGlobal({
  'h1 + .strapline': {
    ...headingStraplineBase,
    fontSize: pxToRem(22),
    lineHeight: pxToRem(32),
    marginTop: pxToRem(-16),
    paddingBottom: pxToRem(48),
  },
  'h2 + .strapline': {
    ...headingStraplineBase,
    fontSize: pxToRem(18),
    lineHeight: pxToRem(28),
    marginTop: pxToRem(-16),
    paddingBottom: pxToRem(40),
  },

  '.strapline > h5': {
    fontWeight: 'var(--font-weight-regular)',
  },

  [mqBreakpoints.tablet]: {
    'h1 + .strapline': {
      ...headingStraplineBase,
      fontSize: pxToRem(24),
      lineHeight: pxToRem(36),
      letterSpacing: '-0.25px',
      marginTop: pxToRem(-32),
      paddingBottom: pxToRem(64),
    },
    'h2 + .strapline': {
      ...headingStraplineBase,
      fontSize: pxToRem(21),
      lineHeight: pxToRem(28),
      letterSpacing: '-0.2px',
      marginTop: pxToRem(-16),
      paddingBottom: pxToRem(48),
    },
    '.display-1 + .strapline': {
      ...displayStrapline,
    },
    '.display-2 + .strapline': {
      ...displayStrapline,
    },
    '.display-3 + .strapline': {
      ...displayStrapline,
    },
    '.display-4 + .strapline': {
      ...displayStrapline,
    },
  },
});

// Numeric

injectGlobal({
  '.numeric': {
    fontFamily: 'var(--font-heading)',
  },
});
